/* PageFlipComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Prop, State, h, Event, EventEmitter } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import PageFlipTemplate from './cwc-page-flip.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { PageFlip } from 'page-flip';
import UtilFullscreen from '@cewe-designsystem/util_fullscreen';

type PhotobookDetails = {
  Height: number;
  Width: number;
  IsOldSample: number;
};

type PhotobookData = {
  details: PhotobookDetails;
  urls: string[];
};

interface PageFlipEvent {
  productId: number;
}

interface PageFlipFullscreenEvent extends PageFlipEvent {
  fullscreenActive: boolean;
}

type PageFlipPageEventSource = 'book' | 'control';

interface PageFlipPageEvent extends PageFlipEvent {
  source: PageFlipPageEventSource;
  fromPage: number;
  toPage: number;
  pageCount: number;
}

@Component({
  tag: 'cwc-page-flip',
  styleUrl: './cwc-page-flip.scss',
  shadow: false,
})
export class PageFlipComponent {
  private readonly API_URL = 'https://api.cewe-community.com/v2/products/';

  private readonly PAGE_WIDTH = 1024;

  private fullscreenUtil: UtilFullscreen = UtilFullscreen.getInstance(document);

  /**
   * The current visible page of the book.
   */
  private currentPage: number = 0;

  /**
   * Is the page change (previous/next) triggered by a control?
   * @private
   */
  private pageChangeEventTriggeredByControl = false;

  /**
   * element
   */
  @Element() element: HTMLElement;

  /**
   * productId
   *
   * @docs
   * @property {number} productId
   */
  @Prop({ reflect: false }) productId: number = 65428;

  /**
   * url
   *
   * @docs
   * @property {string} url
   */
  @Prop() url: string;

  /**
   * controls
   *
   * @docs
   * @property {boolean} controls
   */
  @Prop({ reflect: false }) showControls: boolean = false;

  /**
   * Is the fullscreen mode enabled?
   *
   * @docs
   * @property {boolean} fullscreen
   */
  @Prop({ reflect: true }) fullscreen: boolean = false;

  /**
   * @docs
   * @var {PageFlip} book
   */
  private book: PageFlip;

  /**
   * Details from loaded product (book)
   *
   * @docs
   * @state {PhotobookDetails} details
   */
  @State() details?: PhotobookDetails;

  /**
   * Cover-Pages from loaded product (book)
   *
   * @docs
   * @state {string} cover_front
   * @state {string} cover_back
   */
  @State() coverFront: string = '';
  @State() coverBack: string = '';

  /**
   * Inner-Pages from loaded product (book)
   *
   * @docs
   * @state {string[]} pages
   */
  @State() pages: string[] = [];

  /**
   * Event thrown if the page flip's fullscreen mode is entered/exited.
   *
   * @docs
   * @event on:page-flip:fullscreen-exit
   */
  @Event({
    eventName: 'on:page-flip:fullscreen-change',
  })
  onPageFlipFullscreenChange: EventEmitter;

  /**
   * Event thrown if the page flip is switched to another page.
   *
   * @docs
   * @event on:page-flip:page-change
   */
  @Event({
    eventName: 'on:page-flip:page-change',
  })
  onPageFlipPageChange: EventEmitter;

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  async componentWillLoad() {
    if (!this.productId) {
      console.error('No productId available for page-flip');
      return;
    }

    this.pages = await this.loadPages();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {
    if (!this.productId) {
      return;
    }

    const elementId = 'book' + this.productId;
    const element = this.getContent().querySelector('#' + elementId);

    if (!element) {
      console.error(`Unable to find element with ID ${elementId}`);
      return;
    }

    this.book = new PageFlip(element, {
      width: this.details.Width, // base page width, e.g. 1024
      height: this.details.Height, // base page height, e.g. 787
      size: 'stretch', // or "fixed"
      maxShadowOpacity: 0.5, // half shadow intensity
      showCover: true,
      mobileScrollSupport: false, // disable content scrolling on mobile devices
    });

    this.book.loadFromHTML(this.getContent().querySelectorAll('.flip-book-page'));

    this.book.on('flip', (e) => {
      const targetPage = e.data;

      // Trigger event informing the subscribers about the switch to another page.
      const eventPayload = {
        source: this.pageChangeEventTriggeredByControl ? 'control' : 'book',
        productId: this.productId,
        fromPage: this.currentPage,
        toPage: targetPage,
        pageCount: this.book.getPageCount(),
      } as PageFlipPageEvent;

      this.onPageFlipPageChange.emit(eventPayload);

      this.currentPage = targetPage;
      this.pageChangeEventTriggeredByControl = false;
    });

    // Recognise if the fullscreen mode is entered/exited.
    this.fullscreenUtil.onFullscreenChanged((payload) => {
      if (payload.event.target !== this.getContent()) {
        // It is not the current element which has been put to fullscreen.
        return;
      }

      this.fullscreen = payload.fullscreenActive;

      // Trigger event informing the subscribers about the entered/exited fullscreen mode.
      const eventPayload = {
        productId: this.productId,
        fullscreenActive: this.fullscreen,
      } as PageFlipFullscreenEvent;

      this.onPageFlipFullscreenChange.emit(eventPayload);

      // Dispatch a resize event so that other components are able to fit the new fullscreen size.
      setTimeout(function () {
        window.dispatchEvent(new CustomEvent('resize'));
      }, 500); /* eslint-disable-line no-magic-numbers */
    });
  }

  enterFullscreen() {
    this.fullscreenUtil.enterFullscreen(this.getContent());
  }

  exitFullscreen() {
    this.fullscreenUtil.exitFullscreen();
  }

  gotoNextPage() {
    this.pageChangeEventTriggeredByControl = true;
    this.book.flipNext();
  }

  gotoPreviousPage() {
    this.pageChangeEventTriggeredByControl = true;
    this.book.flipPrev();
  }

  private async loadPages(): Promise<string[]> {
    return fetch(this.API_URL + this.productId, { method: 'GET' })
      .then((result) => result.json())
      .then((json) => json.data as PhotobookData)
      .then((data) => {
        this.details = data.details as PhotobookDetails;

        // const pages = data.urls.map(page => page + `&width=${this.PAGE_WIDTH}`);

        /* IsOldSample has special needs - Get param without toxic &-char */
        let pageSuffix: string = '';
        if (data.details.IsOldSample == 1) {
          pageSuffix = `?width=${this.PAGE_WIDTH}`;
        } else {
          pageSuffix = `&width=${this.PAGE_WIDTH}`;
        }
        const pages = data.urls.map((page) => page + pageSuffix);

        this.coverFront = pages.shift();
        this.coverBack = pages.pop();

        /* IsOldSample has special needs - No leading white page needed */
        if (data.details.IsOldSample == 1) {
          pages.shift();
        }

        return pages;
      });
  }

  /**
   * render
   */
  render() {
    return <PageFlipTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(PageFlipComponent.prototype, AbstractComponent.prototype);

export interface PageFlipComponent extends AbstractComponent {}
